/**
 * State History Types
 *
 * Reusable pattern for tracking state transitions with full audit trail.
 * Follows event-sourcing principles - the history array is append-only.
 *
 * Benefits over individual timestamp fields:
 * - Decoupled from state names (adding/removing states doesn't change schema)
 * - Full audit trail (can track re-sends, retries, repeated transitions)
 * - Metadata per transition (can store context like decline reason, actor)
 * - Reusable across entities (Intro, Relationship, EventGrouping, etc.)
 */

import { z } from "zod";
import type { JsonObjectType } from "../types/json-value.types.js";

type StatusHistoryEntryBase = z.infer<
  ReturnType<typeof statusHistoryEntrySchema<z.ZodString>>
>;

export type StatusHistoryEntry<TState extends string = string> = Omit<
  StatusHistoryEntryBase,
  "state"
> & { readonly state: TState };

interface StatusHistoryEntryLike {
  readonly state: string;
  readonly timestamp: Date;
}

export const StatusHistoryEntry = {
  create<TState extends string>(
    state: TState,
    metadata: JsonObjectType | null = null,
    timestamp: Date = new Date(),
  ): StatusHistoryEntry<TState> {
    return {
      state,
      timestamp,
      metadata,
    };
  },

  findByState<TEntry extends StatusHistoryEntryLike>(
    history: readonly TEntry[],
    state: string,
  ): TEntry | undefined {
    return history.find((entry) => entry.state === state);
  },

  findLatestByState<TEntry extends StatusHistoryEntryLike>(
    history: readonly TEntry[],
    state: string,
  ): TEntry | undefined {
    return [...history].reverse().find((entry) => entry.state === state);
  },

  getCurrentState(
    history: readonly StatusHistoryEntryLike[],
  ): string | undefined {
    const lastEntry = history.at(-1);
    return lastEntry?.state;
  },

  getTimestampForState(
    history: readonly StatusHistoryEntryLike[],
    state: string,
  ): Date | undefined {
    return StatusHistoryEntry.findByState(history, state)?.timestamp;
  },

  hasBeenInState(
    history: readonly StatusHistoryEntryLike[],
    state: string,
  ): boolean {
    return history.some((entry) => entry.state === state);
  },

  countStateOccurrences(
    history: readonly StatusHistoryEntryLike[],
    state: string,
  ): number {
    return history.filter((entry) => entry.state === state).length;
  },

  getAllEntriesForState<TEntry extends StatusHistoryEntryLike>(
    history: readonly TEntry[],
    state: string,
  ): readonly TEntry[] {
    return history.filter((entry) => entry.state === state);
  },

  /**
   * Calculate time spent in a state, from its first occurrence to the next transition.
   * @returns Duration in milliseconds, or null if:
   *   - The state was never entered
   *   - The state is still the current state (no next transition)
   */
  getTimeInState(
    history: readonly StatusHistoryEntryLike[],
    state: string,
  ): number | null {
    const entryIndex = history.findIndex((entry) => entry.state === state);
    if (entryIndex === -1) return null;
    if (entryIndex === history.length - 1) return null;

    const currentEntry = history[entryIndex];
    const nextEntry = history[entryIndex + 1];
    if (currentEntry === undefined || nextEntry === undefined) return null;

    const enteredAt = currentEntry.timestamp.getTime();
    const exitedAt = nextEntry.timestamp.getTime();
    return exitedAt - enteredAt;
  },
} as const;

// Status-history entry schema for domain/view schemas. `z.coerce.date()` accepts
// both Date (server re-parse of a domain object) and ISO string (frontend parse of
// wire JSON), so a single schema covers every boundary.

export function statusHistoryEntrySchema<S extends z.ZodType>(stateSchema: S) {
  return z.object({
    state: stateSchema,
    timestamp: z.coerce.date(),
    metadata: z.record(z.string(), z.json()).nullable().default(null),
  });
}
